import { Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { FormlyFieldConfig } from '@ngx-formly/core'
import { ApiService, BatchIdDropdownProvider, DropdownDatasource, DropdownProvider, EntityNameIdDropdownProvider, UsernameDropdownProviderNew } from '@plantandfood/kup.core'
import { UserGroupsService } from 'app/api/services/user-groups.service'
import { TrialIdDropdownProviderNew } from 'app/dropdown-provider/trial-id-dropdown-provider'
import { BehaviorSubject, Subscription } from 'rxjs'
import { first } from 'rxjs/operators'
import { RequestStatus } from '../../app/constants/request-type.constants'
import { serviceList } from '../services/service-list'

@Injectable()
export class RequestsFormFactory {
    fields: FormlyFieldConfig[]
    form = new FormGroup({})
    users
    private recipientSubject = new BehaviorSubject([])
    private subscription: Subscription

    constructor(private service: ApiService, private userGroupService: UserGroupsService) {
        this.users = new UsernameDropdownProviderNew(this.service)
    }

    requestsFilterForm() {
        const status = [
            { value: RequestStatus.OPEN, label: 'Open' },
            { value: RequestStatus.UNDER_REVIEW, label: 'Under Review' },
            { value: RequestStatus.AWAITING_FEEDBACK, label: 'Awaiting Feedback' },
            { value: RequestStatus.CREATING_BIOMATERIALS, label: 'Creating Biomaterials' },
            { value: RequestStatus.IN_PROGRESS, label: 'In Progress' },
            { value: RequestStatus.COMPLETED, label: 'Completed' },
        ]

        const userList = DropdownProvider.resolve(this.users, {
            suppressRefresh: false,
        }).data$

        const obs = this.userGroupService.getUserGroups()
        this.subscription = obs.subscribe(res => {
            if (res.isOk) {
                const recipient = DropdownDatasource.fieldMap(res.result, 'name')
                this.recipientSubject.next(recipient)
                if (this.subscription) this.subscription.unsubscribe()
            }
        })

        return [
            {
                key: 'name',
                id: 'field_name',
                type: 'input',
                templateOptions: {
                    label: 'Filter by Name',
                },
            },
            {
                key: 'status',
                id: 'field_status',
                type: 'ng-select',
                templateOptions: {
                    multiple: true,
                    label: 'Filter By Status',
                    options: status,
                },
            },
            {
                key: 'requester',
                id: 'field_filter_requester',
                type: 'ng-select',
                templateOptions: {
                    multiple: true,
                    label: 'Filter By Requester',
                    options: userList,
                },
            },
            {
                key: 'recipient',
                id: 'field_filter_recipient',
                type: 'ng-select',
                templateOptions: {
                    multiple: true,
                    label: 'Filter By Recipient',
                    options: this.recipientSubject.asObservable(),
                },
            },
        ]
    }

    requestEditForm(externalReferences, isCreateMode: boolean, canEdit: boolean, trialOperationsGroups, isRecipient: boolean) {
        const biomaterialCreationMethods = new EntityNameIdDropdownProvider(this.service, 'unique/biomaterialCreationMethods')
        const trials = new TrialIdDropdownProviderNew(this.service, 'EDIT')
        const sourceBatches = new EntityNameIdDropdownProvider(this.service, 'unique/biomaterialBatches')
        this.recipientSubject.next(trialOperationsGroups)

        const trial = DropdownProvider.resolve(trials, { suppressRefresh: false })
        const sourceBatch = DropdownProvider.resolve(sourceBatches, { suppressRefresh: false })

        const userData = DropdownProvider.resolve(this.users, {
            suppressRefresh: false,
        })
        const creationMethod = DropdownProvider.resolve(biomaterialCreationMethods, {
            suppressRefresh: false,
        })

        this.fields = [
            {
                key: 'name',
                id: 'field_request_name',
                type: 'input',
                templateOptions: {
                    label: 'Name',
                    disabled: true,
                },
            },
            {
                key: 'creationMethodId',
                id: 'field_creation_method',
                type: 'ng-select',
                templateOptions: {
                    label: 'Creation Method',
                    required: true,
                    options: creationMethod.data$,
                    provider: creationMethod,
                    isTypeahead: true,
                },
            },
            {
                key: 'trialId',
                id: 'field_trial_id',
                type: 'ng-select',
                templateOptions: {
                    label: 'Trial',
                    options: trial.data$,
                    provider: trial,
                    isTypeahead: true,
                    required: true,
                    onChange: item => {
                        if (item) {
                            const obs = this.service.apiService(serviceList.GET_TRIAL_OPERATIONS, null, null, `/${item.value}/trialOperationsGroups`)
                            obs.pipe(first()).subscribe(res => {
                                if (res.isOk) {
                                    const trialOperationsGroups = res.result._embedded.trialOperationsGroups
                                    const recipient = DropdownDatasource.fieldMap(trialOperationsGroups, 'operationsGroupName')

                                    this.recipientSubject.next(recipient)
                                    this.form.patchValue({
                                        recipient: undefined,
                                    })
                                }
                            })
                        }
                    },
                },
                modelOptions: {
                    debounce: {
                        default: 500,
                    },
                },
            },
            {
                key: 'purpose',
                id: 'field_request_purpose',
                type: 'textarea',
                templateOptions: {
                    label: 'Purpose',
                    required: true,
                    rows: 3,
                    maxLength: 255,
                },
            },
            {
                key: 'requester',
                id: 'field_requester',
                type: 'ng-select',
                templateOptions: {
                    label: 'Requester',
                    required: true,
                    options: userData.data$,
                    provider: userData,
                    isTypeahead: true,
                },
            },
            {
                key: 'recipient',
                id: 'field_recipient',
                type: 'ng-select',
                templateOptions: {
                    label: 'Recipient (Operations Group)',
                    required: true,
                    options: this.recipientSubject.asObservable(),
                    description: 'Only Trials with associated Operations Group will have Recipient',
                },
                expressionProperties: {
                    'templateOptions.disabled': '!model.trialId' && `${canEdit}`,
                },
                hooks: {
                    onInit: field => {
                        if (!isRecipient) {
                            field.formControl.reset()
                            field.formControl.markAsTouched()
                        }
                    },
                },
            },
            {
                key: 'requestDate',
                id: 'field_request_date',
                type: 'datepicker',
                className: 'standard-datepicker',
                templateOptions: {
                    label: 'Request Date',
                    required: true,
                    disabled: isCreateMode,
                },
            },
            {
                key: 'sourceBatchId',
                id: 'field_source_batch_id',
                type: 'ng-select',
                templateOptions: {
                    label: 'Source Batch',
                    disabled: isCreateMode,
                    required: true,
                    provider: sourceBatch,
                    options: sourceBatch.data$,
                    isTypeahead: true,
                },
            },
            {
                key: 'completionDate',
                id: 'field_request_completion_date',
                type: 'input',
                templateOptions: {
                    label: 'Completion Date',
                    disabled: true,
                },
            },
            {
                key: 'externalReferences',
                id: 'field_extRef',
                type: 'repeat',
                templateOptions: {
                    addText: 'Add',
                },
                fieldArray: {
                    fieldGroup: [
                        {
                            key: 'namespaceName',
                            id: 'field_namespace_name',
                            type: 'ng-select',
                            className: 'inline-external-reference',
                            templateOptions: {
                                label: 'External Reference Type',
                                options: externalReferences,
                            },
                        },
                        {
                            key: 'name',
                            id: 'field_extRef_name',
                            type: 'input',
                            className: 'inline-external-reference align-with-type',
                            templateOptions: {
                                label: 'External Reference Value',
                            },
                            expressionProperties: {
                                'templateOptions.disabled': '!model.namespaceName',
                            },
                        },
                    ],
                },
            },
        ]
    }

    batchPrintForm() {
        const batches = new BatchIdDropdownProvider(this.service)

        return (this.fields = [
            {
                key: 'batchId',
                id: 'field_source_batch_id',
                type: 'ng-select',
                templateOptions: {
                    label: 'Biomaterial Batch',
                    required: true,
                    disabled: true,
                    options: DropdownProvider.resolve(batches, { suppressRefresh: false }).data$,
                },
            },
        ])
    }
}
